import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n.toLocaleString("en-NZ");

export default function AwningDetailPage() {
  const { slug } = useParams();
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/awning`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  const hasOptions = model?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: model?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/awnings/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom, priceOnRequest: model.priceOnRequest }
  );

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/awnings" replace />;

  const related = models
    .filter((m) => m.type === model.type && m.slug !== model.slug)
    .slice(0, 3);

  const gallery = model.gallery && model.gallery.length > 0 ? model.gallery.map(normalizeGalleryImage) : [{ src: model.thumb, alt: null, type: "image", pos: "center" }];
  const canBuyNow = !model.priceOnRequest && model.inStock !== false;

  return (
    <div data-testid="awning-detail-page">
      <SEO
        path={`/awnings/${model.slug}`}
        title={`${model.name} · NZ`}
        description={`${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero || model.thumb}
        jsonLd={buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero || model.thumb, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/awnings/${model.slug}` })}
      />

      {/* HERO */}
      <section className="relative min-h-[65svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero || model.thumb} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "50% center"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/awnings" data-testid="awning-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)] transition-colors">
              ← Awnings
            </Link>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/80 bg-black/60 backdrop-blur-sm px-2.5 py-1">
              {model.type === "awning" ? "AWNING" : "ACCESSORY"}
            </span>
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.tagline}
          </p>
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            <div>
              {model.priceWas && (
                <p className="font-mono text-sm text-white/50 line-through">${fmt(model.priceWas)}</p>
              )}
              <p className="font-display text-4xl md:text-5xl text-white leading-none">
                {(model.priceFrom && !hasOptions) && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                ${fmt(hasOptions ? optionsState.totalPrice : model.price)}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                GST included · {model.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/awnings/${model.slug}`}
                    testId="awning-cta-buy-now"
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                    disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                  />
                  <AddToCartButton
                    productPath={`/awnings/${model.slug}`}
                    testId="awning-cta-add-to-cart"
                    name={model.name}
                    thumb={model.thumb}
                    unitPrice={hasOptions ? optionsState.totalPrice : model.price}
                    priceFrom={model.priceFrom && !hasOptions}
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                  />
                </>
              )}
              <a href="#enquire" data-testid="awning-cta-enquire" className="btn-secondary">
                Enquire About This <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
          {hasOptions && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={model.optionGroups} variantStock={model.variantStock || []} basePrice={model.price} onChange={setOptionsState} />
            </div>
          )}
        </div>
      </section>

      {/* GALLERY + INTRO */}
      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>

          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Awning</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">{model.intro}</p>
            </div>
            {model.features && model.features.length > 0 && (
              <div className="border-t border-[var(--border-default)] pt-8">
                <p className="overline mb-5">— Key Features</p>
                <ul className="space-y-3">
                  {model.features.map((f, i) => (
                    <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                      <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY IT'S DIFFERENT */}
      {model.contentSections && model.contentSections.length > 0 && (
        <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-16 md:py-20">
            <div className="mb-12 max-w-2xl">
              <p className="overline mb-4">— Why It's Different</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                BUILT TO A <span className="text-[var(--brand-primary)]">DIFFERENT STANDARD.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12">
              {model.contentSections.map((section, i) => (
                <div
                  key={section.heading || i}
                  data-testid={`content-section-${i}`}
                  className={
                    section.highlight
                      ? "lg:col-span-2 border-l-4 border-[var(--brand-primary)] bg-black/30 pl-6 py-5"
                      : "border-t border-[var(--border-default)] pt-6"
                  }
                >
                  <h3 className={section.highlight ? "font-display text-2xl md:text-3xl text-[var(--brand-primary)] mb-3" : "font-display text-xl md:text-2xl text-white mb-3"}>
                    {section.heading}
                  </h3>
                  {section.body && (
                    <p className={section.highlight ? "text-white text-base md:text-lg leading-relaxed" : "text-[var(--text-secondary)] text-base md:text-lg leading-relaxed"}>
                      {section.body}
                    </p>
                  )}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {section.bullets.map((b, bi) => (
                        <li key={b || bi} className="flex items-start gap-2 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                          <Check size={14} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.linkHref && section.linkText && (
                    <Link
                      to={section.linkHref}
                      data-testid={`content-section-link-${i}`}
                      className="inline-flex items-center gap-1.5 mt-4 text-sm font-mono text-[var(--brand-primary)] hover:underline"
                    >
                      {section.linkText} <ArrowUpRight size={14} />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPECS */}
      {model.specs && model.specs.length > 0 && (
        <section className="border-y border-[var(--border-default)] bg-[var(--bg-main)]">
          <div className="container-x py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <p className="overline mb-4">— Specifications</p>
                <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                  THE <span className="text-[var(--brand-primary)]">NUMBERS.</span>
                </h2>
              </div>
              <div className="lg:col-span-8">
                <dl className="divide-y divide-[var(--border-default)] border-t border-b border-[var(--border-default)]">
                  {model.specs.map(([k, v], i) => (
                    <div key={k || i} data-testid={`spec-row-${i}`} className="grid grid-cols-3 gap-4 py-4">
                      <dt className="font-mono text-xs uppercase tracking-wider text-[var(--brand-primary)] col-span-1">{k}</dt>
                      <dd className="text-base md:text-lg text-white col-span-2">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
          <div className="mb-8">
            <p className="overline mb-3">— You Might Also Consider</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE <span className="text-[var(--brand-primary)]">{model.type === "awning" ? "AWNINGS" : "ACCESSORIES"}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/awnings/${r.slug}`}
                data-testid={`related-awning-${i}`}
                className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceFrom ? "FROM " : ""}${fmt(r.price)}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      {/* ENQUIRY */}
      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire About This</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Vehicle fit, wall-kit compatibility, availability, delivery — get a proper answer from someone who lives this stuff.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Awning — ${model.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
